import * as React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import { useNavigate } from 'react-router-dom';
import { useAccount, useAuth } from '../../auth';
import { useLogout } from '../../data/useLogout';
import Typography from '@mui/material/Typography';
import {
  capitalize,
  Divider,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { theme } from '../../utils/theme';
import { useGlobalSettings } from '../../app/GlobalSettingProvider';
import { AvatarProps, StringAvatarProps, User } from '../../types/global.types';

const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

const stringAvatar = (user: User): StringAvatarProps => {
  let children;
  if (user.display_name) {
    const tokens = user.display_name.split(' ');
    children = tokens[0][0].toUpperCase();
    if (tokens.length > 1) {
      children = children + tokens[tokens.length - 1][0];
    }
  } else {
    children = user.email.split('@')[0][0].toUpperCase();
  }

  return {
    sx: {
      bgcolor: stringToColor(user.display_name || user.email),
    },
    children,
  };
};

export const AvatarMenu: React.FC<AvatarProps> = ({ sx }) => {
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const auth = useAuth();
  const [avatarAnchorEl, setAvatarAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const { currentAccount } = useAccount();
  const { settings, setSettings } = useGlobalSettings();

  if (logoutMutation.isSuccess) {
    navigate('/login', { replace: true });
  }
  const accountRole = auth.user.roles.find(
    (role: { account: { id: string }; role: string }) =>
      role.account.id === currentAccount.id,
  )?.role;

  const avatarMenuOpen = Boolean(avatarAnchorEl);
  const handleAvatarMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarAnchorEl(event.currentTarget);
  };
  const handleAvatarMenuClose = () => {
    setAvatarAnchorEl(null);
  };

  const handleLogoutClick = () => {
    logoutMutation.mutate();
  };

  return (
    <div style={{ display: 'flex', alignSelf: 'flex-end', cursor: 'pointer' }}>
      <div
        data-cy="avatar-menu"
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        onClick={handleAvatarMenuClick}
      >
        <Avatar
          {...stringAvatar(auth.user)}
          aria-controls={avatarMenuOpen ? 'avatar-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={avatarMenuOpen ? 'true' : undefined}
          style={{
            marginLeft: '15px',
            marginBottom: '4px',
            ...sx,
          }}
        />
        <div
          style={{
            marginLeft: theme.spacing(1.5),
            display: 'inline-block',
            width: '150px',
          }}
        >
          <Typography variant="body1" color="initial">
            {auth.user.display_name}
          </Typography>
          <Typography variant="caption" color="textSecondary" padding={0}>
            {accountRole
              ?.split('-')
              .map((s: string) => capitalize(s))
              .join(' ')}
          </Typography>
        </div>
      </div>

      <Menu
        sx={{ width: '215px' }}
        id="avatar-menu"
        anchorEl={avatarAnchorEl}
        open={avatarMenuOpen}
        onClose={handleAvatarMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <Divider />
        <MenuItem data-cy="logout-menu-item" onClick={handleLogoutClick}>
          Logout
        </MenuItem>
        {window.location.search.includes('debug') && (
          <MenuItem>
            <small>
              UI:
              <a
                style={{ paddingLeft: '5px' }}
                href={`https://github.com/aquifer-cloud/aquifer-frontend/commit/${process.env['NX_PUBLIC_COMMIT_HASH']}`}
              >
                {process.env['NX_PUBLIC_COMMIT_HASH']}
              </a>
            </small>
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};
